<template>
    <div class="space-y-1.5">
        <!-- Label -->
        <label
            v-if="label"
            :for="inputId"
            class="block text-sm font-medium text-secondary-700 dark:text-secondary-300"
        >
            {{ label }}
            <span v-if="required" class="text-danger-500 ml-0.5" aria-label="required">*</span>
            <span v-if="optional && !required" class="text-secondary-500 dark:text-secondary-400 text-xs ml-1">(Optional)</span>
        </label>

        <!-- Input Container -->
        <div class="relative">
            <!-- Left Icon -->
            <div
                v-if="$slots['icon-left']"
                class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                :class="type === 'textarea' ? 'pt-2.5' : ''"
            >
                <slot name="icon-left" />
            </div>

            <!-- Input / Textarea -->
            <component
                :is="type === 'textarea' ? 'textarea' : 'input'"
                :id="inputId"
                :type="type !== 'textarea' ? actualInputType : undefined"
                :value="modelValue"
                @input="handleInput"
                @blur="handleBlur"
                @focus="handleFocus"
                :placeholder="placeholder"
                :disabled="disabled"
                :readonly="readonly"
                :required="required"
                :maxlength="maxLength"
                :rows="rows"
                :class="inputClasses"
                :aria-invalid="!!error"
                :aria-describedby="ariaDescribedBy"
                :aria-required="required"
                v-bind="$attrs"
            />

            <!-- Right Icon / Action -->
            <div
                v-if="$slots['icon-right'] || (type === 'password' && showPasswordToggle)"
                class="absolute inset-y-0 right-0 pr-3 flex items-center"
                :class="type === 'textarea' ? 'pt-2.5' : ''"
            >
                <!-- Password Toggle -->
                <button
                    v-if="type === 'password' && showPasswordToggle"
                    type="button"
                    @click="togglePasswordVisibility"
                    class="text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-300 focus:outline-none"
                    tabindex="-1"
                >
                    <svg v-if="passwordVisible" class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                    <svg v-else class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                </button>

                <!-- Custom Right Icon -->
                <slot v-else name="icon-right" />
            </div>

            <!-- Success Indicator -->
            <div
                v-if="success && !error"
                class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                :class="type === 'textarea' ? 'pt-2.5' : ''"
            >
                <svg class="h-5 w-5 text-success-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                </svg>
            </div>

            <!-- Error Indicator -->
            <div
                v-if="error"
                class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                :class="type === 'textarea' ? 'pt-2.5' : ''"
            >
                <svg class="h-5 w-5 text-danger-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                </svg>
            </div>
        </div>

        <!-- Help Text -->
        <p
            v-if="helpText && !error"
            :id="`${inputId}-help`"
            class="text-sm text-secondary-500 dark:text-secondary-400"
        >
            {{ helpText }}
        </p>

        <!-- Error Message -->
        <p
            v-if="error"
            :id="`${inputId}-error`"
            class="text-sm text-danger-600 dark:text-danger-400 flex items-start"
            role="alert"
        >
            <svg class="h-4 w-4 mr-1 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
            </svg>
            <span>{{ error }}</span>
        </p>

        <!-- Success Message -->
        <p
            v-if="successMessage"
            :id="`${inputId}-success`"
            class="text-sm text-success-600 dark:text-success-400 flex items-start"
        >
            <svg class="h-4 w-4 mr-1 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
            </svg>
            <span>{{ successMessage }}</span>
        </p>

        <!-- Character Counter (for textarea and text inputs with maxLength) -->
        <div
            v-if="maxLength && showCounter"
            class="flex justify-end"
        >
            <p
                class="text-xs"
                :class="[
                    characterCount > maxLength * 0.9 ? 'text-warning-600 dark:text-warning-400 font-medium' : 'text-secondary-500 dark:text-secondary-400',
                    characterCount === maxLength ? 'text-danger-600 dark:text-danger-400 font-semibold' : ''
                ]"
            >
                {{ characterCount }} / {{ maxLength }}
            </p>
        </div>
    </div>
</template>

<script setup>
import { computed, ref, useSlots } from 'vue';

const props = defineProps({
    // v-model value
    modelValue: {
        type: [String, Number],
        default: '',
    },
    // Input type
    type: {
        type: String,
        default: 'text',
        validator: (value) => ['text', 'email', 'password', 'number', 'tel', 'url', 'date', 'textarea'].includes(value),
    },
    // Input ID (auto-generated if not provided)
    id: {
        type: String,
        default: null,
    },
    // Label text
    label: {
        type: String,
        default: null,
    },
    // Placeholder text
    placeholder: {
        type: String,
        default: null,
    },
    // Help text shown below input
    helpText: {
        type: String,
        default: null,
    },
    // Error message
    error: {
        type: String,
        default: null,
    },
    // Success message
    successMessage: {
        type: String,
        default: null,
    },
    // Success state (shows checkmark)
    success: {
        type: Boolean,
        default: false,
    },
    // Disabled state
    disabled: {
        type: Boolean,
        default: false,
    },
    // Readonly state
    readonly: {
        type: Boolean,
        default: false,
    },
    // Required field
    required: {
        type: Boolean,
        default: false,
    },
    // Show optional label
    optional: {
        type: Boolean,
        default: false,
    },
    // Maximum character length
    maxLength: {
        type: Number,
        default: null,
    },
    // Show character counter
    showCounter: {
        type: Boolean,
        default: true,
    },
    // Textarea rows
    rows: {
        type: Number,
        default: 4,
    },
    // Show password toggle button
    showPasswordToggle: {
        type: Boolean,
        default: true,
    },
});

const emit = defineEmits(['update:modelValue', 'blur', 'focus']);

// Generate unique ID if not provided
const inputId = computed(() => {
    return props.id || `input-${Math.random().toString(36).substr(2, 9)}`;
});

// Password visibility toggle
const passwordVisible = ref(false);
const actualInputType = computed(() => {
    if (props.type === 'password' && passwordVisible.value) {
        return 'text';
    }
    return props.type;
});

const togglePasswordVisibility = () => {
    passwordVisible.value = !passwordVisible.value;
};

// Character count
const characterCount = computed(() => {
    return props.modelValue?.toString().length || 0;
});

// Access slots from setup context
const slots = useSlots();

// Input classes
const inputClasses = computed(() => {
    const baseClasses = 'block w-full rounded-lg border transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-0 dark:bg-secondary-900 dark:text-white';

    // Size classes
    const sizeClasses = props.type === 'textarea' ? 'px-3 py-2.5' : 'px-4 py-2.5';

    // Icon padding
    const leftPadding = slots['icon-left'] ? 'pl-10' : '';
    const rightPadding = (slots['icon-right'] || props.error || props.success || (props.type === 'password' && props.showPasswordToggle)) ? 'pr-10' : '';

    // State-based classes
    let stateClasses = '';
    if (props.error) {
        stateClasses = 'border-danger-300 dark:border-danger-600 focus:border-danger-500 focus:ring-danger-500 text-secondary-900 dark:text-white placeholder-danger-300 dark:placeholder-danger-500';
    } else if (props.success) {
        stateClasses = 'border-success-300 dark:border-success-600 focus:border-success-500 focus:ring-success-500 text-secondary-900 dark:text-white';
    } else {
        stateClasses = 'border-secondary-300 dark:border-secondary-600 focus:border-primary-500 focus:ring-primary-500 text-secondary-900 dark:text-white placeholder-secondary-400 dark:placeholder-secondary-500';
    }

    // Disabled/readonly classes
    let interactionClasses = '';
    if (props.disabled) {
        interactionClasses = 'bg-secondary-100 dark:bg-secondary-800 cursor-not-allowed opacity-60';
    } else if (props.readonly) {
        interactionClasses = 'bg-secondary-50 dark:bg-secondary-800/50 cursor-default';
    } else {
        interactionClasses = 'bg-white dark:bg-secondary-900';
    }

    return [
        baseClasses,
        sizeClasses,
        leftPadding,
        rightPadding,
        stateClasses,
        interactionClasses,
    ].filter(Boolean).join(' ');
});

// ARIA describedby
const ariaDescribedBy = computed(() => {
    const ids = [];
    if (props.helpText && !props.error) ids.push(`${inputId.value}-help`);
    if (props.error) ids.push(`${inputId.value}-error`);
    if (props.successMessage) ids.push(`${inputId.value}-success`);
    return ids.length > 0 ? ids.join(' ') : undefined;
});

// Event handlers
const handleInput = (event) => {
    let value = event.target.value;

    // Handle number type
    if (props.type === 'number') {
        value = value === '' ? '' : Number(value);
    }

    emit('update:modelValue', value);
};

const handleBlur = (event) => {
    emit('blur', event);
};

const handleFocus = (event) => {
    emit('focus', event);
};
</script>

<style scoped>
/* Smooth transitions */
* {
    transition-property: color, background-color, border-color, opacity;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-duration: 200ms;
}

/* Textarea resize */
textarea {
    resize: vertical;
}

/* Remove number input arrows */
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

input[type="number"] {
    -moz-appearance: textfield;
}

/* Autofill styling */
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus {
    -webkit-text-fill-color: inherit;
    -webkit-box-shadow: 0 0 0px 1000px transparent inset;
    transition: background-color 5000s ease-in-out 0s;
}
</style>
